import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Legend, ReferenceLine, Customized } from 'recharts';
import { getFileTestDate } from '../../util/GenericUtil';
import { calculateAllSystemScores } from '../../util/SystemScoringUtil';
import { convertDateToTimestamp, applyMinimumGapConstraint } from '../../util/TimeBasedScaling';
import { useScreen } from '../../context/ScreenProvider';
import bloodGroupings from '../../../lambda/health-recommendations/blood_groupings.json';
import { useFilters } from '../../context/FilterProvider';

// Muted system colors (similar to BoxPlot's 0.4 opacity style)
const SYSTEM_COLORS = {
    blood_sugar: 'rgba(139, 92, 246, 0.7)',   // muted purple
    cardio: 'rgba(239, 68, 68, 0.7)',          // muted red
    liver: 'rgba(245, 158, 11, 0.7)',          // muted amber
    kidney: 'rgba(16, 185, 129, 0.7)',         // muted emerald
    digestion: 'rgba(249, 115, 22, 0.7)',      // muted orange
    bone: 'rgba(107, 114, 128, 0.7)',          // muted gray
    red_blood_cells: 'rgba(220, 38, 38, 0.7)', // muted dark red
    immune: 'rgba(59, 130, 246, 0.7)',         // muted blue
    thyroid: 'rgba(6, 182, 212, 0.7)',         // muted cyan
    inflammation: 'rgba(236, 72, 153, 0.7)',    // muted pink
    hormones: 'rgba(168, 85, 247, 0.7)'          // muted violet
};

const DEFAULT_COLOR = 'rgba(153, 153, 153, 0.7)';

// Custom error bar renderer with unique keys (avoids recharts' coordinate-based keys)
const CustomErrorBars = ({ data, dataKey, systemId, xAxisMap, yAxisMap, color, opacity, strokeWidth, capWidth }) => {
    if (!data || !xAxisMap || !yAxisMap || opacity === 0) return null;

    const xAxis = Object.values(xAxisMap)[0];
    const yAxis = Object.values(yAxisMap)[0];
    if (!xAxis || !yAxis) return null;

    return (
        <g className="custom-error-bars">
            {data.map((entry, index) => {
                const errorData = entry[dataKey];
                const score = entry[systemId];
                if (!errorData || score == null) return null;

                const [lowerError, upperError] = errorData;
                if (lowerError === 0 && upperError === 0) return null;

                const x = xAxis.scale(entry.adjustedTimestamp);
                const yTop = yAxis.scale(Math.min(100, score + upperError));
                const yBottom = yAxis.scale(Math.max(0, score - lowerError));

                if (isNaN(x) || isNaN(yTop) || isNaN(yBottom)) return null;

                return (
                    <g key={`error-${systemId}-${index}-${entry.testdate}`}>
                        {/* Vertical line */}
                        <line
                            x1={x} y1={yTop} x2={x} y2={yBottom}
                            stroke={color}
                            strokeWidth={strokeWidth}
                            strokeOpacity={opacity}
                        />
                        {/* Top cap */}
                        <line
                            x1={x - capWidth / 2} y1={yTop} x2={x + capWidth / 2} y2={yTop}
                            stroke={color}
                            strokeWidth={strokeWidth}
                            strokeOpacity={opacity}
                        />
                        {/* Bottom cap */}
                        <line
                            x1={x - capWidth / 2} y1={yBottom} x2={x + capWidth / 2} y2={yBottom}
                            stroke={color}
                            strokeWidth={strokeWidth}
                            strokeOpacity={opacity}
                        />
                    </g>
                );
            })}
        </g>
    );
};

// Color utility functions
const getSystemColor = (systemId) => SYSTEM_COLORS[systemId] || DEFAULT_COLOR;
const getOutlineColor = (color) => color.replace('0.7)', '1)');
const getDisplayName = (systemId) => bloodGroupings[systemId]?.display_name || systemId;

// Reusable circle indicator component
const CircleIndicator = ({ color }) => (
    <span style={{
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        backgroundColor: color,
        border: `2px solid ${getOutlineColor(color)}`,
        flexShrink: 0
    }} />
);

// Empty state component
const EmptyState = ({ message }) => (
    <div style={{
        padding: '40px',
        textAlign: 'center',
        color: '#666',
        fontStyle: 'italic'
    }}>
        {message}
    </div>
);

// Calculate optimal items per row based on container width
// Prefer 2 rows when possible, but allow more rows for narrow containers
const calculateItemsPerRow = (totalItems, containerWidth = null) => {
    if (totalItems <= 5) return totalItems;

    // If container width provided, calculate based on actual space
    if (containerWidth) {
        const minItemWidth = 100; // Minimum width per legend item
        const maxCols = Math.floor(containerWidth / minItemWidth);
        if (maxCols >= totalItems) return totalItems; // All fit in one row
        if (maxCols < 2) return 1; // Very narrow - single column
        // Prefer 2 rows, but allow more if needed
        const targetRows = 2;
        const itemsPerRow = Math.ceil(totalItems / targetRows);
        if (itemsPerRow <= maxCols) return itemsPerRow;
        // Need more rows - normalize across them
        return maxCols;
    }

    // Fallback: target 2 rows
    return Math.ceil(totalItems / 2);
};

function OrganSystemLinePlot({ bloodTestFiles, getFxRange, sex = 'male', selectedSystem, onSystemClick, selectedFileIndex, hideLegend = false }) {
    const { selectFile } = useFilters();
    const { isPhone } = useScreen();
    const [hoveredSystem, setHoveredSystem] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [containerWidth, setContainerWidth] = useState(null);
    const containerRef = useRef(null);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        if (!dropdownOpen) return;
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [dropdownOpen]);

    // Container width measurement for legend layout
    useEffect(() => {
        const handleResize = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };
        handleResize(); // Initial measurement
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Calculate optimal legend columns - prefer 2 rows (pure calculation, no side effects)
    const legendColumns = useMemo(() => {
        if (isPhone) return null;
        return calculateItemsPerRow(Object.keys(bloodGroupings).length, containerWidth);
    }, [isPhone, containerWidth]);

    // Process data: group by date and calculate system scores
    const chartData = useMemo(() => {
        if (!bloodTestFiles || bloodTestFiles.length === 0) return [];

        // Group tests by date
        const fileData = {};
        bloodTestFiles.forEach(file => {
            if (!file.data || !file.data.body) return;

            const testDate = getFileTestDate(file);
            if (!testDate) return;

            if (!fileData[testDate]) {
                fileData[testDate] = [];
            }
            fileData[testDate].push(...file.data.body);
        });

        const sortedTestDates = Object.keys(fileData).sort();

        if (sortedTestDates.length === 0) return [];

        // Wrap getFxRange to include sex parameter
        const getFxRangeWithSex = (markerName) => getFxRange(markerName, sex);

        // Calculate system scores for each date
        const dataPoints = sortedTestDates.map(testDate => {
            const testsForDate = fileData[testDate];
            const systemScores = calculateAllSystemScores(
                testsForDate,
                bloodGroupings,
                getFxRangeWithSex
            );

            const point = {
                testdate: testDate,
                timestamp: convertDateToTimestamp(testDate)
            };

            // Dynamically add all system scores and pre-compute error bar values
            Object.entries(systemScores).forEach(([systemId, data]) => {
                const score = data.score;
                const confidence = data.confidence.percentage;
                point[systemId] = score;
                point[`${systemId}_confidence`] = confidence;

                // Pre-compute error bar values to avoid function recalculation
                if (score != null && confidence != null) {
                    const barLength = (100 - confidence) / 2;
                    const lower = Math.max(0, score - barLength);
                    const upper = Math.min(100, score + barLength);
                    point[`${systemId}_error`] = [score - lower, upper - score];
                } else {
                    point[`${systemId}_error`] = [0, 0];
                }
            });

            return point;
        });

        // Filter out dates with no organ system data
        const dataPointsWithData = dataPoints.filter(point => {
            return Object.keys(bloodGroupings).some(systemId => point[systemId] != null);
        });

        // Apply time-based spacing
        const adjustedData = applyMinimumGapConstraint(dataPointsWithData);

        return adjustedData;
    }, [bloodTestFiles, getFxRange, sex]);

    // Get all available system IDs dynamically
    const systemIds = useMemo(() => {
        return Object.keys(bloodGroupings);
    }, []);

    // Find selected date's adjusted timestamp for reference line
    const selectedTimestamp = useMemo(() => {
        if (selectedFileIndex == null || !bloodTestFiles?.[selectedFileIndex]) return null;
        const selectedDate = getFileTestDate(bloodTestFiles[selectedFileIndex]);
        if (!selectedDate) return null;
        // Use String() to handle type differences (testdate can be number or string)
        const matchingPoint = chartData.find(point => String(point.testdate) === String(selectedDate));
        return matchingPoint?.adjustedTimestamp ?? null;
    }, [selectedFileIndex, bloodTestFiles, chartData]);

    // Handle line/legend click to filter to single system - syncs with other charts
    const handleLineClick = useCallback((systemId) => {
        if (onSystemClick) {
            // Toggle: if already selected, deselect; otherwise select
            onSystemClick(selectedSystem === systemId ? null : systemId);
        }
    }, [onSystemClick, selectedSystem]);

    // Handle dot click to select the corresponding test date
    const handleDotClick = useCallback((data) => {
        if (!data?.payload?.testdate || !bloodTestFiles) return;
        const clickedDate = String(data.payload.testdate);
        const fileIndex = bloodTestFiles.findIndex(file => {
            const fileDate = getFileTestDate(file);
            return String(fileDate) === clickedDate;
        });
        if (fileIndex >= 0) {
            selectFile(fileIndex);
        }
    }, [bloodTestFiles, selectFile]);

    // Memoized tick formatter for XAxis - shortened format (e.g., "Jan 2024")
    const formatXAxisTick = useCallback((timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    }, []);

    // Custom legend renderer with CSS grid for even distribution
    const renderLegend = useCallback((props) => {
        const { payload } = props;
        // Filter out hit area lines (they have transparent stroke)
        const visiblePayload = payload.filter(entry => entry.color !== 'transparent');

        return (
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: legendColumns
                        ? `repeat(${legendColumns}, minmax(0, 1fr))`
                        : 'repeat(auto-fit, minmax(80px, 1fr))',
                    gap: '4px 12px',
                    padding: '8px',
                    marginTop: '8px',
                    overflow: 'hidden'
                }}
            >
                {visiblePayload.map((entry, index) => {
                    const systemId = entry.dataKey;
                    const isSelected = selectedSystem === systemId;
                    const isHovered = hoveredSystem === systemId;

                    return (
                        <div
                            key={`legend-${index}`}
                            onClick={() => handleLineClick(systemId)}
                            onMouseEnter={() => setHoveredSystem(systemId)}
                            onMouseLeave={() => setHoveredSystem(null)}
                            style={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: isSelected ? '4px 8px' : '4px 0',
                                opacity: isSelected ? 1 : isHovered ? 0.85 : 0.7,
                                backgroundColor: isSelected ? '#e5e7eb' : 'transparent',
                                borderRadius: isSelected ? '12px' : 0,
                                transition: 'opacity 0.15s ease, background-color 0.15s ease, padding 0.15s ease',
                                minWidth: 0
                            }}
                        >
                            <CircleIndicator color={entry.color} />
                            <span style={{
                                color: isSelected ? '#111' : '#333',
                                fontSize: '0.8em',
                                fontWeight: isSelected ? 600 : 500,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}>
                                {getDisplayName(systemId)}
                            </span>
                        </div>
                    );
                })}
            </div>
        );
    }, [legendColumns, selectedSystem, hoveredSystem, handleLineClick]);

    if (!chartData || chartData.length === 0) {
        return <EmptyState message="Upload multiple blood tests to see organ system trends over time" />;
    }

    if (chartData.length === 1) {
        return <EmptyState message="Multiple test dates required for trend visualization" />;
    }

    return (
        <div ref={containerRef} style={{ width: '100%', position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <ResponsiveContainer width="100%" height="95%">
                <LineChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
                    <XAxis
                        dataKey="adjustedTimestamp"
                        type="number"
                        domain={['dataMin', 'dataMax']}
                        allowDataOverflow={true}
                        tickFormatter={formatXAxisTick}
                        style={{ fontSize: '0.85em' }}
                        padding={{ left: 10, right: 10 }}
                   />
                    <YAxis
                        domain={[0, 100]}
                        allowDataOverflow={true}
                        style={{ fontSize: '0.85em' }}
                        width={35}
                   />
                    {/* Desktop: interactive legend chips, Mobile: handled outside chart */}
                    {!isPhone && !hideLegend && <Legend content={renderLegend} />}

                    {/* Reference line indicating selected test date */}
                    {selectedTimestamp != null && (
                        <ReferenceLine
                            x={selectedTimestamp}
                            stroke="#666"
                            strokeDasharray="4 4"
                            strokeWidth={2}
                        />
                    )}

                    {/* Invisible hit area lines for click/hover interactivity */}
                    {systemIds.map(systemId => {
                        const isSelected = selectedSystem === systemId;
                        const isSelectable = !selectedSystem || isSelected;

                        return (
                            <Line
                                key={`hitarea-${systemId}`}
                                type="monotone"
                                dataKey={systemId}
                                stroke="transparent"
                                strokeWidth={10}
                                dot={false}
                                activeDot={false}
                                legendType="none"
                                connectNulls={true}
                                isAnimationActive={false}
                                onMouseEnter={() => !isPhone && isSelectable && setHoveredSystem(systemId)}
                                onMouseLeave={() => !isPhone && isSelectable && setHoveredSystem(null)}
                                onClick={() => isSelectable && handleLineClick(systemId)}
                                style={{ cursor: isSelectable ? 'pointer' : 'default', pointerEvents: isSelectable ? 'auto' : 'none' }}
                           />
                        );
                    })}

                    {/* Visible lines rendered on top */}
                    {systemIds.map(systemId => {
                        const color = getSystemColor(systemId);
                        const isHovered = hoveredSystem === systemId;
                        const isSelected = selectedSystem === systemId;

                        // Responsive sizing
                        const baseStrokeWidth = isPhone ? 1 : 2;
                        const hoverStrokeWidth = isPhone ? 1.5 : 3;
                        const dotRadius = isPhone ? 2 : 4;

                        // Determine opacity based on interaction state
                        // Selected: only show selected line, hide all others
                        // Hover (desktop): highlight hovered line, slightly dim others
                        let opacity = 1;
                        let strokeWidth = baseStrokeWidth;

                        if (selectedSystem) {
                            opacity = isSelected ? 1 : 0;
                            strokeWidth = isSelected ? hoverStrokeWidth : baseStrokeWidth;
                        } else if (hoveredSystem && !isPhone) {
                            opacity = isHovered ? 1 : 0.65;
                            strokeWidth = isHovered ? hoverStrokeWidth : baseStrokeWidth;
                        }

                        return (
                            <Line
                                key={systemId}
                                type="monotone"
                                dataKey={systemId}
                                stroke={color}
                                strokeWidth={strokeWidth}
                                strokeOpacity={opacity}
                                dot={{ r: dotRadius, fill: color, fillOpacity: opacity, style: { cursor: 'pointer', pointerEvents: 'auto' } }}
                                activeDot={opacity > 0 ? (dotProps) => (
                                    <circle
                                        cx={dotProps.cx}
                                        cy={dotProps.cy}
                                        r={dotRadius + 2}
                                        fill={color}
                                        stroke={getOutlineColor(color)}
                                        strokeWidth={2}
                                        style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                                        onClick={() => handleDotClick(dotProps)}
                                    />
                                ) : false}
                                connectNulls={true}
                                isAnimationActive={false}
                                style={{ pointerEvents: 'none' }}
                            />
                        );
                    })}

                    {/* Custom error bars rendered via Customized to avoid duplicate key warnings */}
                    {systemIds.map(systemId => {
                        const color = getSystemColor(systemId);
                        const isHovered = hoveredSystem === systemId;
                        const isSelected = selectedSystem === systemId;

                        let errorBarOpacity = 0;
                        if (selectedSystem) {
                            errorBarOpacity = isSelected ? 0.6 : 0;
                        } else if (hoveredSystem && !isPhone) {
                            errorBarOpacity = isHovered ? 0.6 : 0;
                        }

                        if (errorBarOpacity === 0) return null;

                        return (
                            <Customized
                                key={`errorbar-${systemId}`}
                                component={(props) => (
                                    <CustomErrorBars
                                        {...props}
                                        data={chartData}
                                        dataKey={`${systemId}_error`}
                                        systemId={systemId}
                                        color={color}
                                        opacity={errorBarOpacity}
                                        strokeWidth={isPhone ? 1 : 2}
                                        capWidth={isPhone ? 4 : 8}
                                    />
                                )}
                            />
                        );
                    })}
                </LineChart>
            </ResponsiveContainer>

            {/* Phone: custom dropdown for filtering (at bottom like legend) */}
            {isPhone && !hideLegend && (
                <div ref={dropdownRef} style={{ marginTop: '8px', position: 'relative' }}>
                    <button
                        onClick={() => setDropdownOpen(prev => !prev)}
                        style={{
                            width: '100%',
                            padding: '8px 10px',
                            borderRadius: '4px',
                            border: '1px solid #ccc',
                            fontSize: '0.85em',
                            backgroundColor: 'white',
                            cursor: 'pointer',
                            boxSizing: 'border-box',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {selectedSystem ? (
                                <>
                                    <CircleIndicator color={getSystemColor(selectedSystem)} />
                                    {getDisplayName(selectedSystem)}
                                </>
                            ) : 'All Systems'}
                        </span>
                        <span style={{ fontSize: '0.7em' }}>{dropdownOpen ? '▲' : '▼'}</span>
                    </button>
                    {dropdownOpen && (
                        <div style={{
                            position: 'absolute',
                            bottom: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            marginBottom: '4px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
                            zIndex: 100
                        }}>
                            <div
                                onClick={() => { onSystemClick && onSystemClick(null); setDropdownOpen(false); }}
                                style={{
                                    padding: '8px 10px',
                                    cursor: 'pointer',
                                    backgroundColor: !selectedSystem ? '#f0f0f0' : 'white',
                                    borderBottom: '1px solid #eee'
                                }}
                            >
                                All Systems
                            </div>
                            {systemIds.map(systemId => (
                                <div
                                    key={systemId}
                                    onClick={() => { onSystemClick && onSystemClick(systemId); setDropdownOpen(false); }}
                                    style={{
                                        padding: '8px 10px',
                                        cursor: 'pointer',
                                        backgroundColor: selectedSystem === systemId ? '#f0f0f0' : 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                >
                                    <CircleIndicator color={getSystemColor(systemId)} />
                                    <span>{getDisplayName(systemId)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default OrganSystemLinePlot;
